import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import { Header } from '../components/Header';

const TermsOfService = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(null);

  // Define sections for the terms of service
  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      content: `
        <p>Welcome to My Football Game. These Terms of Service ("Terms") govern your access to and use of the My Football Game website, mobile application, and services (collectively, the "Service").</p>
        <p>By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the Service.</p>
      `
    },
    {
      id: 'account',
      title: 'Account Registration and Security',
      content: `
        <p>To access certain features of the Service, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.</p>
        <p>You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. We encourage you to use "strong" passwords (passwords that use a combination of upper and lower case letters, numbers, and symbols) with your account.</p>
        <p>You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.</p>
      `
    },
    {
      id: 'gameplay',
      title: 'Gameplay and Virtual Assets',
      content: `
        <p>My Football Game offers a simulation experience where users can build and manage virtual football clubs. Virtual currency, items, and achievements acquired in the game have no real-world value and remain the property of My Football Game.</p>
        <p>We reserve the right to modify, adjust balancing, or remove any aspect of gameplay, virtual assets, or in-game economy without prior notice.</p>
        <p>Users may not sell, trade, or transfer virtual assets outside of the game. Any attempt to do so is a violation of these Terms and may result in account termination.</p>
      `
    },
    {
      id: 'purchases',
      title: 'Purchases and Subscription Terms',
      content: `
        <p>The Service may include the option to purchase virtual currency, premium features, or subscription plans using real money. All purchases are final and non-refundable, except as required by applicable law.</p>
        <p>Subscriptions automatically renew unless auto-renew is turned off at least 24 hours before the end of the current period. Your account will be charged for renewal within 24 hours prior to the end of the current period.</p>
        <p>Prices for all products and services are subject to change with notice. We reserve the right to modify or discontinue any aspect of the Service at any time.</p>
      `
    },
    {
      id: 'conduct',
      title: 'User Conduct and Community Guidelines',
      content: `
        <p>As a My Football Game user, you agree not to:</p>
        <ul>
          <li>Use the Service for any illegal purpose or in violation of any local, state, national, or international law</li>
          <li>Harass, abuse, or harm another person, including sending unwelcome communications to others</li>
          <li>Impersonate My Football Game staff or other users or provide false information</li>
          <li>Attempt to circumvent any content-filtering techniques, security measures, or rate limits</li>
          <li>Use cheats, automation software, hacks, mods, or any unauthorized third-party software</li>
          <li>Develop or use any applications that interact with the Service without prior written consent</li>
          <li>Attempt to access areas or features of the Service that you are not authorized to access</li>
        </ul>
        <p>Violation of these guidelines may result in temporary or permanent suspension of your account.</p>
      `
    },
    {
      id: 'privacy',
      title: 'Privacy and Data Use',
      content: `
        <p>Your privacy is important to us. Our Privacy Policy, which is incorporated into these Terms, explains how we collect, use, and share information about you when you access or use our Services.</p>
        <p>By using the Service, you consent to the collection, use, and sharing of your information as described in our Privacy Policy.</p>
        <p>We may use data about your interactions with the game to improve the service, analyze game balance, and provide personalized experiences.</p>
      `
    },
    {
      id: 'intellectual',
      title: 'Intellectual Property Rights',
      content: `
        <p>The Service and its original content, features, and functionality are and will remain the exclusive property of My Football Game and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.</p>
        <p>Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of My Football Game.</p>
        <p>You acknowledge and agree that you have no ownership rights in and to any game content, including any virtual assets, accounts, statistics, or virtual property.</p>
      `
    },
    {
      id: 'termination',
      title: 'Termination and Account Deletion',
      content: `
        <p>We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including if you breach the Terms.</p>
        <p>Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service, or request account deletion through the settings page.</p>
        <p>All provisions of the Terms which by their nature should survive termination shall survive termination, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.</p>
      `
    },
    {
      id: 'changes',
      title: 'Changes to Terms',
      content: `
        <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide notice of changes by posting the updated terms on this page and updating the "last updated" date.</p>
        <p>Your continued use of the Service after any such changes constitutes your acceptance of the new Terms. Please review these Terms periodically for changes.</p>
      `
    },
    {
      id: 'contact',
      title: 'Contact Information',
      content: `
        <p>If you have any questions about these Terms, please contact us:</p>
        Email: andreas.thomae@kampanos.pt
      `
    }
  ];

  const handleExpandToggle = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  return (
    <motion.div 
      className="w-full min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <Header />

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 shadow-xl mb-8"
        >
          <p className="text-white/90">
            Please read these Terms of Service carefully before using the My Football Game game and related services. By accessing or using our service, you agree to be bound by these terms.
          </p>
        </motion.div>

        {/* Accordion sections */}
        <div className="space-y-4">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + (index * 0.1) }}
              className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => handleExpandToggle(section.id)}
                className="w-full px-5 py-4 text-left flex justify-between items-center hover:bg-white/[0.05] transition-colors"
              >
                <h2 className="text-lg font-semibold">{section.title}</h2>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`w-5 h-5 transition-transform ${expanded === section.id ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {expanded === section.id && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-5 py-4 border-t border-white/10 text-white/80"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              )}
            </motion.div>
          ))}
        </div>

        {/* Agreement section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mt-10 bg-gradient-to-r from-blue-900/30 to-indigo-900/30 p-6 rounded-xl border border-blue-500/20"
        >
          <h3 className="text-xl font-bold mb-3 text-center">Your Agreement</h3>
          <p className="text-white/80 text-center mb-6">
            By using My Football Game, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')} 
              className="px-6 py-2.5 bg-[var(--accent)] hover:bg-[var(--accent)] rounded-lg font-medium transition-colors shadow-md"
            >
              Create Account
            </button>
            <button
              onClick={() => navigate('/')} 
              className="px-6 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
            >
              Return to Home
            </button>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <Footer />
    </motion.div>
  );
};

export default TermsOfService;